import React, { useState, useEffect, useMemo } from "react";
import { gql, useQuery } from "@apollo/client";
import NavBar from "../../components/common/NavBar";
import PaginatedFlatTable from "../../components/pagination/PaginatedFlatTable";
import { formattedDateTime } from "../../util/date";
import FeeCalculator from "../../components/stripe/FeeCalculator";
import { GlobalStyles } from "../../constants/styles";
import useActiveProfile from "../../components/common/hooks/useActiveProfile";
import useThemeColors from "../../components/organization/portal/useThemeColors";
import { alphaFromHex } from "../../util/colors";
import useTranslation from "../../i18n/useTranslation";

// 1) Organizations (leagues, divisions, seasons)
const ORGANIZATIONS_QUERY = gql`
    query Organizations($profileId: Int!) {
        organizations(profileId: $profileId, page: 1, pageSize: 1000) {
            objects {
                id
                slug
                name
                divisions {
                    id
                    name
                    seasons {
                        id
                        name
                    }
                }
            }
        }
    }
`;

// 2) Payments for selected org/division/season/status
const PAYMENTS_QUERY = gql`
    query OrganizationPayments(
        $organizationSlug: String!
        $divisionId: Int
        $seasonId: Int
        $status: [String!]
        $page: Int
        $pageSize: Int
    ) {
        organizationPayments(
            organizationSlug: $organizationSlug
            divisionId: $divisionId
            seasonId: $seasonId
            status: $status
            page: $page
            pageSize: $pageSize
        ) {
            objects {
                id
                amount
                description
                currency
                organization {
                    name
                }
                season {
                    id
                    name
                }
                profile {
                    fullName
                }
                status
                dueDate
                createdAt
                contentTypeModel
                relatedObjectStr
                sanction {
                    id
                    type
                    reason
                    status
                    gamesSuspended
                    issuedAt
                    profile {
                        id
                        fullName
                    }
                    team {
                        id
                        name
                    }
                    game {
                        id
                        name
                    }
                }
            }
            page
            pages
            hasNext
            hasPrev
            totalAmount
            totalPaid
            totalOutstanding
            overallTotalAmount
            overallTotalPaid
            overallTotalOutstanding
        }
    }
`;

export default function FinancialDashboardPage() {
    const colors = useThemeColors();
    const { activeProfile } = useActiveProfile();
    const { t } = useTranslation();

    // Filters
    const [selectedLeagueId, setSelectedLeagueId] = useState("");
    const [selectedDivisionId, setSelectedDivisionId] = useState("");
    const [selectedSeasonId, setSelectedSeasonId] = useState("");
    const [selectedStatus, setSelectedStatus] = useState("pending");

    // receive live numbers from the reusable calculator to show in the info banner
    const [liveCalc, setLiveCalc] = useState({
        currency: "USD",
        amountPaid: 5.0,
        appFeePct: 3,
        stripePct: 2.9,
        stripeFixed: 0.3,
        appFee: 0.25,
        toLeagueBeforeStripe: 4.75,
        stripeFee: 0.44,
        leagueNet: 4.31,
    });

    // Pagination
    const [page, setPage] = useState(1);
    const pageSize = 25;

    // ---- First query: get organizations ----
    const { data: orgsData, loading: orgsLoading } = useQuery(ORGANIZATIONS_QUERY, {
        variables: { profileId: activeProfile?.id },
        skip: !activeProfile?.id,
        fetchPolicy: "network-only",
    });

    const leagues = useMemo(() => orgsData?.organizations?.objects || [], [orgsData]);
    const selectedLeague = leagues.find((l) => String(l.id) === String(selectedLeagueId));
    const divisions = selectedLeague?.divisions || [];
    const selectedDivision = divisions.find((d) => String(d.id) === String(selectedDivisionId));
    const seasons = selectedDivision?.seasons || [];

    // Set default league after organizations load
    useEffect(() => {
        if (!selectedLeagueId && leagues.length > 0) setSelectedLeagueId(leagues[0].id);
    }, [leagues, selectedLeagueId]);

    // Prepare variables for payments query
    const organizationSlug = selectedLeague?.slug || "";
    const divisionId = selectedDivisionId || null;
    const seasonId = selectedSeasonId || null;
    const statusVar = selectedStatus ? [selectedStatus] : null;

    // ---- Second query: get payments ----
    const {
        data: paymentsData,
        loading: paymentsLoading,
    } = useQuery(PAYMENTS_QUERY, {
        variables: { organizationSlug, divisionId, seasonId, status: statusVar, page, pageSize },
        skip: !organizationSlug,
        fetchPolicy: "network-only",
    });

    // Data mapping
    const payments = paymentsData?.organizationPayments?.objects || [];
    const currentPage = paymentsData?.organizationPayments?.page || 1;
    const pages = paymentsData?.organizationPayments?.pages || 1;

    const filteredTotal = paymentsData?.organizationPayments?.totalAmount ?? 0;
    const filteredPaid = paymentsData?.organizationPayments?.totalPaid ?? 0;
    const filteredOutstanding = paymentsData?.organizationPayments?.totalOutstanding ?? 0;

    const overallTotal = paymentsData?.organizationPayments?.overallTotalAmount ?? 0;
    const overallPaid = paymentsData?.organizationPayments?.overallTotalPaid ?? 0;
    const overallOutstanding = paymentsData?.organizationPayments?.overallTotalOutstanding ?? 0;

    const timezone = Intl.DateTimeFormat().resolvedOptions().timeZone;

    const navBarElem = {
        pageTitle: t("nav.financial"),
        links: [{ pageTitle: t("nav.financial"), navLink: null, active: true }],
    };

    const fmt = useMemo(
        () =>
            new Intl.NumberFormat(undefined, {
                style: "currency",
                currency: liveCalc.currency || "USD",
                minimumFractionDigits: 2,
                maximumFractionDigits: 2,
            }),
        [liveCalc.currency]
    );

    const statusPillClass = (status) => {
        const s = String(status || "").toUpperCase();
        if (s === "PAID") return "kbola-pill kbola-pill-paid";
        if (s === "PENDING") return "kbola-pill kbola-pill-pending";
        return "kbola-pill kbola-pill-neutral";
    };

    const noValue = t("financial.noValue");

    return (
        <div className="content-wrapper">
            <NavBar navBarElem={navBarElem} />

            <section className="content">
                <div className="container-fluid" id="kbola-financial-scope">
                    {/* Info Message */}
                    <div className="row mb-3">
                        <div className="col-12">
                            <div className="alert alert-info">
                                <i className="icon fas fa-exclamation-triangle"></i>
                                <strong>{t("financial.noticeTitle")}</strong>{" "}
                                {t("financial.noticeText1")} {t("financial.noticeText2")}{" "}
                                <strong>
                                    {liveCalc.stripePct}% + {fmt.format(liveCalc.stripeFixed)}
                                </strong>{" "}
                                {t("financial.noticeText3")}{" "}
                                <strong>{liveCalc.appFeePct}%</strong>{" "}
                                {t("financial.noticeText4")}

                                <div className="mt-2">
                                    <p>
                                        {t("financial.example", {
                                            amount: fmt.format(liveCalc.amountPaid),
                                            net: fmt.format(liveCalc.leagueNet),
                                        })}
                                    </p>
                                </div>
                            </div>
                        </div>
                    </div>

                    {/* FILTERS CARD */}
                    <div className="row mb-3">
                        <div className="col-12">
                            <div className="kbola-card kbola-filters-card">
                                <div className="kbola-card-head">
                                    <div className="kbola-card-head-left">
                                        <span className="kbola-card-badge">{t("financial.filtersBadge")}</span>
                                        <div className="kbola-card-head-text">
                                            <div className="kbola-card-title">{t("financial.paymentsTitle")}</div>
                                            <div className="kbola-card-subtitle">
                                                {t("financial.filtersSubtitle")}
                                            </div>
                                        </div>
                                    </div>

                                    <div className="kbola-card-head-right">
                                        <button
                                            type="button"
                                            className="kbola-btn kbola-btn-ghost"
                                            onClick={() => {
                                                setSelectedDivisionId("");
                                                setSelectedSeasonId("");
                                                setSelectedStatus("pending");
                                                setPage(1);
                                            }}
                                            title={t("financial.resetFilters")}
                                            disabled={!selectedLeagueId}
                                        >
                                            <i className="fas fa-undo" />
                                            {t("financial.reset")}
                                        </button>
                                    </div>
                                </div>

                                <div className="kbola-card-body">
                                    <div className="row">
                                        <div className="col-md-3 col-6 mb-3">
                                            <label className="kbola-label">{t("financial.organization")}</label>
                                            <select
                                                className="form-control kbola-select"
                                                value={selectedLeagueId}
                                                onChange={(e) => {
                                                    setSelectedLeagueId(e.target.value);
                                                    setSelectedDivisionId("");
                                                    setSelectedSeasonId("");
                                                    setPage(1);
                                                }}
                                                disabled={orgsLoading}
                                            >
                                                <option value="">{t("financial.selectLeague")}</option>
                                                {leagues.map((l) => (
                                                    <option key={l.id} value={l.id}>
                                                        {l.name}
                                                    </option>
                                                ))}
                                            </select>
                                        </div>

                                        <div className="col-md-3 col-6 mb-3">
                                            <label className="kbola-label">{t("financial.division")}</label>
                                            <select
                                                className="form-control kbola-select"
                                                value={selectedDivisionId}
                                                onChange={(e) => {
                                                    setSelectedDivisionId(e.target.value);
                                                    setSelectedSeasonId("");
                                                    setPage(1);
                                                }}
                                                disabled={!selectedLeagueId}
                                            >
                                                <option value="">{t("financial.selectDivision")}</option>
                                                {divisions.map((d) => (
                                                    <option key={d.id} value={d.id}>
                                                        {d.name}
                                                    </option>
                                                ))}
                                            </select>
                                        </div>

                                        <div className="col-md-3 col-6 mb-3">
                                            <label className="kbola-label">{t("financial.season")}</label>
                                            <select
                                                className="form-control kbola-select"
                                                value={selectedSeasonId}
                                                onChange={(e) => {
                                                    setSelectedSeasonId(e.target.value);
                                                    setPage(1);
                                                }}
                                                disabled={!selectedDivisionId}
                                            >
                                                <option value="">{t("financial.selectSeason")}</option>
                                                {seasons.map((s) => (
                                                    <option key={s.id} value={s.id}>
                                                        {s.name}
                                                    </option>
                                                ))}
                                            </select>
                                        </div>

                                        <div className="col-md-3 col-6 mb-3">
                                            <label className="kbola-label">{t("financial.status")}</label>
                                            <select
                                                className="form-control kbola-select"
                                                value={selectedStatus}
                                                onChange={(e) => {
                                                    setSelectedStatus(e.target.value);
                                                    setPage(1);
                                                }}
                                                disabled={!selectedLeagueId}
                                            >
                                                <option value="">{t("financial.all")}</option>
                                                <option value="pending">{t("financial.pending")}</option>
                                                <option value="paid">{t("financial.paid")}</option>
                                            </select>
                                        </div>
                                    </div>

                                    <div className="kbola-filters-hint">
                                        <i className="fas fa-filter" />
                                        {t("financial.autoRefreshHint")}
                                    </div>
                                </div>
                            </div>
                        </div>
                    </div>

                    {/* SUMMARY CARDS */}
                    <div className="row">
                        <div className="col-lg-4 col-12 mb-3">
                            <div className="kbola-stat kbola-stat-total">
                                <div className="kbola-stat-icon">
                                    <i className="fas fa-wallet" />
                                </div>
                                <div className="kbola-stat-body">
                                    <div className="kbola-stat-title">{t("financial.total")}</div>
                                    <div className="kbola-stat-kpi">{fmt.format(Number(filteredTotal || 0))}</div>
                                    <div className="kbola-stat-sub">
                                        {t("financial.overall")}: <b>{fmt.format(Number(overallTotal || 0))}</b>
                                    </div>
                                </div>
                            </div>
                        </div>

                        <div className="col-lg-4 col-12 mb-3">
                            <div className="kbola-stat kbola-stat-paid">
                                <div className="kbola-stat-icon">
                                    <i className="fas fa-coins" />
                                </div>
                                <div className="kbola-stat-body">
                                    <div className="kbola-stat-title">{t("financial.paid")}</div>
                                    <div className="kbola-stat-kpi">{fmt.format(Number(filteredPaid || 0))}</div>
                                    <div className="kbola-stat-sub">
                                        {t("financial.overall")}: <b>{fmt.format(Number(overallPaid || 0))}</b>
                                    </div>
                                </div>
                            </div>
                        </div>

                        <div className="col-lg-4 col-12 mb-3">
                            <div className="kbola-stat kbola-stat-outstanding">
                                <div className="kbola-stat-icon">
                                    <i className="fas fa-exclamation-circle" />
                                </div>
                                <div className="kbola-stat-body">
                                    <div className="kbola-stat-title">{t("financial.outstanding")}</div>
                                    <div className="kbola-stat-kpi">{fmt.format(Number(filteredOutstanding || 0))}</div>
                                    <div className="kbola-stat-sub">
                                        {t("financial.overall")}: <b>{fmt.format(Number(overallOutstanding || 0))}</b>
                                    </div>
                                </div>
                            </div>
                        </div>
                    </div>

                    {/* Main Row */}
                    <div className="row">
                        {/* Left: Payments table */}
                        <div className="col-lg-9 col-12 mb-4">
                            <PaginatedFlatTable
                                title={t("financial.tableTitle")}
                                page={currentPage}
                                pages={pages}
                                onPageChange={setPage}
                                items={payments}
                                loading={paymentsLoading}
                                headers={[
                                    t("financial.tableHeaders.status"),
                                    t("financial.tableHeaders.amount"),
                                    t("financial.tableHeaders.description"),
                                    t("financial.tableHeaders.organization"),
                                    t("financial.tableHeaders.season"),
                                    t("financial.tableHeaders.game"),
                                    t("financial.tableHeaders.relatedObject"),
                                    t("financial.tableHeaders.profile"),
                                    t("financial.tableHeaders.createdAt"),
                                    t("financial.tableHeaders.dueDate"),
                                    t("financial.tableHeaders.sanction"),
                                ]}
                                renderRow={(payment, idx) => (
                                    <tr key={payment.id || idx}>
                                        <td>
                                            <span className={statusPillClass(payment.status)}>
                                                {payment.status || noValue}
                                            </span>
                                        </td>
                                        <td style={{ whiteSpace: "nowrap" }}>
                                            {fmt.format(Number(payment.amount || 0))}{" "}
                                            <span className="kbola-muted">
                                                ({payment.currency || liveCalc.currency || "USD"})
                                            </span>
                                        </td>
                                        <td>{payment.description || noValue}</td>
                                        <td>{payment.organization?.name || noValue}</td>
                                        <td>{payment.season?.name || noValue}</td>
                                        <td>{payment.sanction?.game?.name || noValue}</td>
                                        <td>{payment.relatedObjectStr || noValue}</td>
                                        <td>{payment.profile?.fullName || noValue}</td>
                                        <td>
                                            {payment.createdAt
                                                ? formattedDateTime(payment.createdAt, timezone)
                                                : noValue}
                                        </td>
                                        <td>
                                            {payment.dueDate
                                                ? formattedDateTime(payment.dueDate, timezone)
                                                : noValue}
                                        </td>
                                        <td>
                                            {payment.sanction ? (
                                                <span>
                                                    {payment.sanction.type} – {payment.sanction.reason}
                                                </span>
                                            ) : (
                                                noValue
                                            )}
                                        </td>
                                    </tr>
                                )}
                            />
                        </div>

                        {/* Right: Calculator */}
                        <div className="col-lg-3 col-12 mb-4">
                            <FeeCalculator
                                defaultCurrency={liveCalc.currency}
                                defaultAmount={liveCalc.amountPaid}
                                defaultAppFeePct={liveCalc.appFeePct}
                                defaultStripePct={liveCalc.stripePct}
                                defaultStripeFixed={liveCalc.stripeFixed}
                                onChange={setLiveCalc}
                                colors={colors}
                            />
                        </div>
                    </div>

                    {/* Page scoped styles */}
                    <style>{`
            /* Scope */
            #kbola-financial-scope{
              font-family: 'Poppins', sans-serif;
            }

            /* FontAwesome safety inside scope */
            #kbola-financial-scope i.fas,
            #kbola-financial-scope i.far,
            #kbola-financial-scope i.fal,
            #kbola-financial-scope i.fa,
            #kbola-financial-scope i[class^="fa-"],
            #kbola-financial-scope i[class*=" fa-"] {
              font-family: "Font Awesome 5 Free" !important;
              font-weight: 900;
            }
            #kbola-financial-scope i.fab {
              font-family: "Font Awesome 5 Brands" !important;
              font-weight: 400;
            }

            /* Info banner */
            #kbola-financial-scope .kbola-info{
              display: flex;
              gap: 12px;
              align-items: flex-start;
              border-radius: 18px;
              border: 1px solid rgba(0,0,0,0.08);
              background: linear-gradient(135deg, rgba(var(--kbs-primary-rgb, 0,173,255), 0.16), rgba(0,0,0,0));
              box-shadow: 0 18px 44px rgba(0,0,0,0.08);
              padding: 12px 14px;
              overflow: hidden;
              position: relative;
            }
            #kbola-financial-scope .kbola-info:before{
              content: "";
              position: absolute;
              inset: 0;
              background: radial-gradient(circle at 20% 0%, rgba(var(--kbs-primary-rgb, 0,173,255), 0.10), transparent 55%);
              pointer-events: none;
            }
            #kbola-financial-scope .kbola-info-icon{
              width: 44px;
              height: 44px;
              border-radius: 14px;
              border: 1px solid rgba(0,0,0,0.10);
              background: rgba(255,255,255,0.75);
              display: inline-flex;
              align-items: center;
              justify-content: center;
              flex: 0 0 auto;
              color: ${GlobalStyles.colors.woodsmoke};
              position: relative;
              z-index: 1;
            }
            #kbola-financial-scope .kbola-info-body{
              min-width: 0;
              position: relative;
              z-index: 1;
            }
            #kbola-financial-scope .kbola-info-title{
              font-weight: 900;
              color: #14151a;
              font-size: 13.5px;
              margin-bottom: 4px;
            }
            #kbola-financial-scope .kbola-info-text{
              font-weight: 700;
              color: rgba(20,21,26,0.72);
              font-size: 12.8px;
              line-height: 1.35;
            }
            #kbola-financial-scope .kbola-info-example{
              margin-top: 8px;
              font-weight: 800;
              color: rgba(20,21,26,0.70);
              font-size: 12.8px;
            }

            /* Filters card */
            #kbola-financial-scope .kbola-card{
              border-radius: 18px;
              overflow: hidden;
              border: 1px solid rgba(0,0,0,0.08);
              background: #fff;
              box-shadow: 0 18px 44px rgba(0,0,0,0.10);
            }
            #kbola-financial-scope .kbola-card-head{
              display: flex;
              align-items: center;
              justify-content: space-between;
              gap: 12px;
              padding: 12px 14px;
              background: linear-gradient(
                    135deg,
                    ${alphaFromHex(colors.primary, 0.50)},
                    ${alphaFromHex(colors.primary, 0.00)}
                ),
              border-bottom: 1px solid rgba(0,0,0,0.06);
            }
            #kbola-financial-scope .kbola-card-head-left{
              display: flex;
              align-items: center;
              gap: 10px;
              min-width: 0;
            }
            #kbola-financial-scope .kbola-card-badge{
              font-size: 12px;
              font-weight: 900;
              letter-spacing: 0.03em;
              padding: 6px 10px;
              border-radius: 999px;
              background: ${colors.primary};
              border: 1px solid rgba(0,0,0,0.10);
              color: ${colors.textOnPrimary};
              white-space: nowrap;
              flex: 0 0 auto;
            }
            #kbola-financial-scope .kbola-card-head-text{
              min-width: 0;
            }
            #kbola-financial-scope .kbola-card-title{
              font-weight: 900;
              color: #14151a;
              font-size: 14px;
              line-height: 1.1;
              white-space: nowrap;
              overflow: hidden;
              text-overflow: ellipsis;
            }
            #kbola-financial-scope .kbola-card-subtitle{
              font-weight: 700;
              color: rgba(20,21,26,0.55);
              font-size: 12.5px;
              margin-top: 2px;
              white-space: nowrap;
              overflow: hidden;
              text-overflow: ellipsis;
            }
            #kbola-financial-scope .kbola-card-body{
              padding: 14px;
            }
            #kbola-financial-scope .kbola-filters-hint{
              margin-top: 2px;
              display: inline-flex;
              align-items: center;
              gap: 8px;
              font-weight: 800;
              color: rgba(20,21,26,0.60);
              font-size: 12.5px;
              padding: 8px 10px;
              border-radius: 12px;
              border: 1px solid rgba(0,0,0,0.06);
              background: rgba(0,0,0,0.015);
            }

            /* Labels + selects */
            #kbola-financial-scope .kbola-label{
              font-weight: 900;
              font-size: 12.5px;
              color: rgba(20,21,26,0.72);
              margin-bottom: 6px;
              display: inline-block;
            }
            #kbola-financial-scope .kbola-select{
              border-radius: 12px;
              border: 1px solid rgba(0,0,0,0.10);
              font-weight: 800;
            }
            #kbola-financial-scope .kbola-select:focus{
              outline: none;
              box-shadow: 0 0 0 0.2rem rgba(var(--kbs-primary-rgb, 0,173,255), 0.18);
              border-color: rgba(var(--kbs-primary-rgb, 0,173,255), 0.65);
            }

            /* Buttons */
            #kbola-financial-scope .kbola-btn{
              border-radius: 12px;
              padding: 10px 12px;
              font-weight: 900;
              display: inline-flex;
              align-items: center;
              gap: 8px;
              border: 1px solid rgba(0,0,0,0.12);
              background: #fff;
              color: #14151a;
              cursor: pointer;
              line-height: 1;
              white-space: nowrap;
            }
            #kbola-financial-scope .kbola-btn:disabled{
              opacity: 0.65;
              cursor: not-allowed;
            }
            #kbola-financial-scope .kbola-btn-ghost{
              background: rgba(0,0,0,0.015);
            }
            #kbola-financial-scope .kbola-btn:focus{
              outline: none;
              box-shadow: 0 0 0 0.2rem rgba(var(--kbs-primary-rgb, 0,173,255), 0.18);
              border-color: rgba(var(--kbs-primary-rgb, 0,173,255), 0.65);
            }

            /* KPI cards */
            #kbola-financial-scope .kbola-stat{
              border-radius: 18px;
              border: 1px solid rgba(0,0,0,0.08);
              background: #fff;
              box-shadow: 0 18px 44px rgba(0,0,0,0.10);
              padding: 12px 14px;
              display: flex;
              align-items: center;
              gap: 12px;
              position: relative;
              overflow: hidden;
              min-height: 86px;
            }
            #kbola-financial-scope .kbola-stat:before{
              content:"";
              position:absolute;
              inset:0;
              background: linear-gradient(
                    135deg,
                    ${alphaFromHex(colors.primary, 0.50)},
                    ${alphaFromHex(colors.primary, 0.00)}
                );
              pointer-events:none;
            }
            #kbola-financial-scope .kbola-stat-icon{
              width: 46px;
              height: 46px;
              border-radius: 16px;
              border: 1px solid rgba(0,0,0,0.10);
              background: ${colors.primary};
              display: inline-flex;
              align-items: center;
              justify-content: center;
              color: ${colors.textOnPrimary};
              flex: 0 0 auto;
              position: relative;
              z-index: 1;
            }
            #kbola-financial-scope .kbola-stat-body{
              min-width: 0;
              position: relative;
              z-index: 1;
            }
            #kbola-financial-scope .kbola-stat-title{
              font-weight: 900;
              color: rgba(20,21,26,0.70);
              font-size: 12.5px;
              text-transform: uppercase;
              letter-spacing: 0.02em;
              margin-bottom: 2px;
            }
            #kbola-financial-scope .kbola-stat-kpi{
              font-weight: 1000;
              color: #14151a;
              font-size: 22px;
              line-height: 1.1;
              margin-bottom: 2px;
              white-space: nowrap;
              overflow: hidden;
              text-overflow: ellipsis;
            }
            #kbola-financial-scope .kbola-stat-sub{
              font-weight: 800;
              font-size: 12.5px;
              color: rgba(20,21,26,0.60);
            }

            /* Color variants */
            #kbola-financial-scope .kbola-stat.kbola-stat-paid{
              border-color: rgba(25,135,84,0.22);
            }
            #kbola-financial-scope .kbola-stat.kbola-stat-paid:before{
              background: linear-gradient(135deg, rgba(25,135,84,0.12), rgba(0,0,0,0));
            }
            #kbola-financial-scope .kbola-stat.kbola-stat-paid .kbola-stat-icon{
              background: rgba(25,135,84,0.10);
              border-color: rgba(25,135,84,0.20);
              color: #198754;
            }
            #kbola-financial-scope .kbola-stat.kbola-stat-paid .kbola-stat-kpi{
              color: #146c43;
            }

            #kbola-financial-scope .kbola-stat.kbola-stat-outstanding{
              border-color: rgba(255,193,7,0.30);
            }
            #kbola-financial-scope .kbola-stat.kbola-stat-outstanding:before{
              background: linear-gradient(135deg, rgba(255,193,7,0.18), rgba(0,0,0,0));
            }
            #kbola-financial-scope .kbola-stat.kbola-stat-outstanding .kbola-stat-icon{
              background: rgba(255,193,7,0.16);
              border-color: rgba(255,193,7,0.28);
              color: #9a6b00;
            }
            #kbola-financial-scope .kbola-stat.kbola-stat-outstanding .kbola-stat-kpi{
              color: #7a5200;
            }

            /* Status pills */
            #kbola-financial-scope .kbola-pill{
              display: inline-flex;
              align-items: center;
              justify-content: center;
              padding: 6px 10px;
              border-radius: 999px;
              border: 1px solid rgba(0,0,0,0.10);
              font-weight: 900;
              font-size: 12px;
              letter-spacing: 0.02em;
              text-transform: uppercase;
              line-height: 1;
              white-space: nowrap;
            }
            #kbola-financial-scope .kbola-pill-paid{
              border-color: rgba(25,135,84,0.25);
              background: rgba(25,135,84,0.10);
              color: #146c43;
            }
            #kbola-financial-scope .kbola-pill-pending{
              border-color: rgba(255,193,7,0.35);
              background: rgba(255,193,7,0.16);
              color: #7a5200;
            }
            #kbola-financial-scope .kbola-pill-neutral{
              border-color: rgba(0,0,0,0.12);
              background: rgba(0,0,0,0.03);
              color: rgba(20,21,26,0.75);
            }

            #kbola-financial-scope .kbola-muted{
              color: rgba(20,21,26,0.55);
              font-weight: 800;
            }
          `}</style>
                </div>
            </section>
        </div>
    );
}
